import stylex from '@stylexjs/stylex';
import { FC, useCallback, useState } from 'react';
import { PlusIcon } from '@shared/assets/icons';
import InputClearable from '@shared/ui/input-clearable';
import { phoneNumberValidator } from '@shared/libs/phone-validator';
import { AddGuestButton } from '../AddGuestButton';
import { ExtraGuestRow } from './ExtraGuestRow';
import { styles } from './styles';

interface ExtraGuestItem {
  id: number;
  lastName: string;
  firstName: string;
  patronymic: string;
}

export interface MainGuestTabErrors {
  lastName?: string;
  firstName?: string;
  patronymic?: string;
  phone?: string;
  email?: string;
}

export interface MainGuestTabProps {
  lastName: string;
  setLastName: (v: string) => void;
  firstName: string;
  setFirstName: (v: string) => void;
  patronymic: string;
  setPatronymic: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  onlyMainGuestSection?: boolean;
  errors?: MainGuestTabErrors;
}

export const MainGuestTab: FC<MainGuestTabProps> = ({
  lastName,
  setLastName,
  firstName,
  setFirstName,
  patronymic,
  setPatronymic,
  phone,
  setPhone,
  email,
  setEmail,
  onlyMainGuestSection = false,
  errors,
}) => {
  const err = errors ?? {};
  const [extraAdults, setExtraAdults] = useState<ExtraGuestItem[]>([]);
  const [extraChildren, setExtraChildren] = useState<ExtraGuestItem[]>([]);

  const addAdult = () =>
    setExtraAdults((prev) => [...prev, { id: Date.now(), lastName: '', firstName: '', patronymic: '' }]);
  const addChild = () =>
    setExtraChildren((prev) => [...prev, { id: Date.now(), lastName: '', firstName: '', patronymic: '' }]);
  const removeAdult = (id: number) => setExtraAdults((prev) => prev.filter((item) => item.id !== id));
  const removeChild = (id: number) => setExtraChildren((prev) => prev.filter((item) => item.id !== id));
  const updateAdult = (id: number, field: keyof Omit<ExtraGuestItem, 'id'>, value: string) => {
    setExtraAdults((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };
  const updateChild = (id: number, field: keyof Omit<ExtraGuestItem, 'id'>, value: string) => {
    setExtraChildren((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const handlePhoneChange = useCallback(
    (value: string) => {
      setPhone(phoneNumberValidator(value));
    },
    [setPhone]
  );

  const handleEmailChange = useCallback(
    (value: string) => {
      setEmail(value.replace(/\s/g, ''));
    },
    [setEmail]
  );

  const mainGuestSection = (
    <section {...stylex.props(styles.section)}>
      <h3 {...stylex.props(styles.sectionTitle)}>Основной гость</h3>
      <div {...stylex.props(styles.mainGuestFields)}>
        <div {...stylex.props(styles.fieldRow)}>
          <div {...stylex.props(styles.fieldWithLabel)}>
            <span {...stylex.props(styles.fieldLabel)}>Фамилия</span>
            <InputClearable
              status={err.lastName ? 'error' : 'default'}
              size="small"
              settings={{ width: '267px' }}
              placeholder="Укажите фамилию"
              value={lastName}
              changeFunction={setLastName}
              clearHandler={() => setLastName('')}
              inputMessage={err.lastName}
            />
          </div>
          <div {...stylex.props(styles.fieldWithLabel)}>
            <span {...stylex.props(styles.fieldLabel)}>Имя</span>
            <InputClearable
              status={err.firstName ? 'error' : 'default'}
              size="small"
              settings={{ width: '267px' }}
              placeholder="Укажите имя"
              value={firstName}
              changeFunction={setFirstName}
              clearHandler={() => setFirstName('')}
              inputMessage={err.firstName}
            />
          </div>
          <div {...stylex.props(styles.fieldWithLabel)}>
            <span {...stylex.props(styles.fieldLabel)}>Отчество</span>
            <InputClearable
              status={err.patronymic ? 'error' : 'default'}
              size="small"
              settings={{ width: '267px' }}
              placeholder="Укажите отчество"
              value={patronymic}
              changeFunction={setPatronymic}
              clearHandler={() => setPatronymic('')}
              inputMessage={err.patronymic}
            />
          </div>
        </div>
        <div {...stylex.props(styles.fieldRow)}>
          <div {...stylex.props(styles.fieldWithLabel)}>
            <span {...stylex.props(styles.fieldLabel)}>Телефон</span>
            <InputClearable
              status={err.phone ? 'error' : 'default'}
              size="small"
              settings={{ width: '267px' }}
              placeholder="+ 7 (000) 000-00-00"
              value={phone}
              changeFunction={handlePhoneChange}
              clearHandler={() => setPhone('')}
              inputMessage={err.phone}
            />
          </div>
          <div {...stylex.props(styles.fieldWithLabel)}>
            <span {...stylex.props(styles.fieldLabel)}>E-mail</span>
            <InputClearable
              status={err.email ? 'error' : 'default'}
              size="small"
              settings={{ width: '267px' }}
              placeholder="Укажите e-mail"
              value={email}
              changeFunction={handleEmailChange}
              clearHandler={() => setEmail('')}
              inputMessage={err.email}
            />
          </div>
        </div>
      </div>
    </section>
  );

  if (onlyMainGuestSection) {
    return <div {...stylex.props(styles.sections)}>{mainGuestSection}</div>;
  }

  const hasAddedGuests = extraAdults.length || extraChildren.length;

  return (
    <div {...stylex.props(styles.sections)}>
      {mainGuestSection}
      <section {...stylex.props(styles.section, styles.sectionExtraGuests)}>
        <h3 {...stylex.props(styles.sectionTitle)}>Дополнительные гости</h3>
        <div {...stylex.props(styles.extraGuestsContent, !hasAddedGuests && styles.extraGuestsContentNoGap)}>
          <div {...stylex.props(styles.adultContainer)}>
            {extraAdults.map((item, index) => (
              <ExtraGuestRow
                key={item.id}
                item={item}
                title={`Взрослый гость ${index + 1}`}
                onUpdate={updateAdult}
                onRemove={removeAdult}
              />
            ))}
            {extraChildren.map((item, index) => (
              <ExtraGuestRow
                key={item.id}
                item={item}
                title={`Маленький гость ${index + 1}`}
                onUpdate={updateChild}
                onRemove={removeChild}
              />
            ))}
          </div>
          <div {...stylex.props(styles.addGuestButtons)}>
            <AddGuestButton icon={<PlusIcon height="20" width="20" />} onClick={addAdult}>
              Взрослый
            </AddGuestButton>
            <AddGuestButton icon={<PlusIcon height="20" width="20" />} onClick={addChild}>
              Маленький
            </AddGuestButton>
          </div>
        </div>
      </section>
    </div>
  );
};
